import { create } from 'zustand';
import type { TrackKind } from '@etown/shared';

/** Klingel-Zustand eines Peers aus lokaler Sicht. */
export type RingState = 'none' | 'incoming' | 'outgoing' | 'declined';

export interface PeerMedia {
  peerId: string;
  displayName: string;
  connection: 'connecting' | 'connected' | 'failed';
  /** true = Medien freigegeben (kein Anklingeln noetig bzw. angenommen). */
  mediaActive: boolean;
  ring: RingState;
  streams: Partial<Record<TrackKind, MediaStream>>;
}

interface MediaState {
  /** Bereitschaft (HUD-Toggle) vs. tatsaechlich laufender Track. */
  micArmed: boolean;
  camArmed: boolean;
  micLive: boolean;
  camLive: boolean;
  screenOn: boolean;
  mediaError: string | null;
  /** Grosse Gespraechsansicht ein-/ausgeklappt. */
  callPanelOpen: boolean;
  peers: Record<string, PeerMedia>;
  setLocal: (
    patch: Partial<
      Pick<MediaState, 'micArmed' | 'camArmed' | 'micLive' | 'camLive' | 'screenOn' | 'mediaError' | 'callPanelOpen'>
    >,
  ) => void;
  upsertPeer: (peer: PeerMedia) => void;
  setPeerRing: (peerId: string, ring: RingState) => void;
  removePeer: (peerId: string) => void;
  reset: () => void;
}

const INITIAL = {
  micArmed: false,
  camArmed: false,
  micLive: false,
  camLive: false,
  screenOn: false,
  mediaError: null,
  callPanelOpen: false,
  peers: {},
} as const;

/**
 * AV-Zustand fuer die React-UI: lokale Toggles und der Stream-Zustand
 * aller verbundenen Peers (befuellt vom RtcManager und von localMedia).
 */
export const useMediaStore = create<MediaState>((set) => ({
  ...INITIAL,

  setLocal: (patch) => set(patch),

  upsertPeer: (peer) =>
    set((state) => ({ peers: { ...state.peers, [peer.peerId]: peer } })),

  setPeerRing: (peerId, ring) =>
    set((state) => {
      const peer = state.peers[peerId];
      if (!peer) return state;
      return { peers: { ...state.peers, [peerId]: { ...peer, ring } } };
    }),

  removePeer: (peerId) =>
    set((state) => {
      const { [peerId]: _removed, ...rest } = state.peers;
      return { peers: rest };
    }),

  reset: () => set({ ...INITIAL }),
}));
